import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Animated, Dimensions, FlatList, FlatListProps, ViewStyle } from 'react-native'

import { isWeb } from '@common/config/env'
import { OVERVIEW_CONTENT_MAX_HEIGHT } from '@common/modules/dashboard/screens/DashboardScreen'
import spacings from '@common/styles/spacings'
import commonWebStyles from '@web/styles/utils/common'

import useBanners from '../../hooks/useBanners'
import { TabType } from '../TabsAndSearch/Tabs/Tab/Tab'

interface Props extends FlatListProps<any> {
  tab: TabType
  openTab: TabType
  animatedOverviewHeight: Animated.Value
}

// We do this instead of unmounting the component to prevent component rerendering when switching tabs.
const HIDDEN_STYLE: ViewStyle = {
  position: 'absolute',
  opacity: 0,
  display: 'none',
  // @ts-ignore
  pointerEvents: 'none'
}

const SCROLLBAR_TRIGGER_THRESHOLD = 4

const getFlatListStyle = (tab: TabType, openTab: TabType, allBannersLength: number) => [
  spacings.ph0,
  commonWebStyles.contentContainer,
  !allBannersLength && spacings.mtTy,
  openTab !== tab ? HIDDEN_STYLE : {}
]

const DashboardPageScrollContainer: FC<Props> = ({
  tab,
  openTab,
  animatedOverviewHeight,
  onScroll,
  ...rest
}) => {
  const [hasScrollBar, setHasScrollBar] = useState(false)
  const controllerBanners = useBanners()
  const flatlistRef = useRef<FlatList | null>(null)

  const style = useMemo(
    () => getFlatListStyle(tab, openTab, controllerBanners.length),
    [controllerBanners.length, openTab, tab]
  )

  const contentContainerStyle = useMemo(() => {
    // The home-v2 card shell (owned by DashboardPages) carries the horizontal inset,
    // so the list itself adds NO horizontal padding to its rows — only a hair on the
    // right for the scrollbar gutter when one is present. flexGrow:1 lets a short list
    // (e.g. a private wallet with few tokens) stretch to fill the card height so the
    // footer sits at the bottom instead of floating mid-card.
    return [
      // When a scrollbar is present, inset the rows by the scrollbar width so the
      // (overlay) indicator no longer paints on top of the row content. 10px clears
      // the widest indicator we render; 0 when there is no scrollbar.
      { paddingRight: hasScrollBar ? 10 : 0 },
      controllerBanners.length ? spacings.ptTy : spacings.pt0,
      // Breathing room below the last row / footer inside the card.
      { flexGrow: 1 }
    ]
  }, [controllerBanners.length, hasScrollBar])

  // Reset scroll position when switching tabs (new)
  useEffect(() => {
    if (!flatlistRef.current) return

    if (openTab === tab) {
      // Scroll to top
      flatlistRef.current?.scrollToOffset({ offset: 0, animated: false })

      // Expand overview
      Animated.spring(animatedOverviewHeight, {
        toValue: OVERVIEW_CONTENT_MAX_HEIGHT,
        bounciness: 0,
        speed: 2.8,
        overshootClamping: true,
        // animatedOverviewHeight drives layout props in DashboardOverview; native
        // animated module rejects those, so this animation must use the JS driver.
        useNativeDriver: false
      }).start()
    }
  }, [animatedOverviewHeight, openTab, tab])

  const handleContentSizeChange = useCallback((contentWidth: number) => {
    const windowWidth = Dimensions.get('window').width

    if (windowWidth - contentWidth > SCROLLBAR_TRIGGER_THRESHOLD) return

    setHasScrollBar(contentWidth < windowWidth)
  }, [])

  return (
    <FlatList
      ref={flatlistRef}
      style={style}
      contentContainerStyle={contentContainerStyle}
      stickyHeaderIndices={[1]} // Makes the header sticky
      removeClippedSubviews
      bounces={false}
      alwaysBounceVertical={false}
      showsVerticalScrollIndicator={false}
      scrollEventThrottle={16}
      onScroll={onScroll}
      onContentSizeChange={handleContentSizeChange}
      {...rest}
    />
  )
}

export default DashboardPageScrollContainer
